<template>
  <div>
    <div
      class="mb-4 d-flex"
      v-for="(item, index) in messages"
      :class="[
        item.user_type,
        item.user_type === 'admin' ? adminMessageClass : memberMessageClass,
      ]"
    >
      <img
        v-if="item.user_type == 'admin'"
        :src="`${publicPath}/img/rcp/icon/cs-profile.png`"
        class="img-circle img-thumbnail p-0 avatar mr-2"
        alt="avatar"
      />
      <div
        class="message w-75 fz-14"
        :class="
          item.user_type == 'admin' ? 'mr-auto bg-upload-payment' : 'ml-auto'
        "
      >
        <!-- Handle Image Files -->
        <div v-if="item.info && getFileType(item.info.fileURL) == 'image'">
          <a :href="item.info.fileURL" target="_blank">
            <img :src="item.info.fileURL" />
          </a>
          <div>{{ getMessage(item.message, item.info.encodeURL) }}</div>
        </div>

        <!-- Handle PDF Files -->
        <div v-else-if="item.info && getFileType(item.info.fileURL) == 'pdf'">
          <a
            :href="item.info.fileURL"
            target="_blank"
            class="d-flex align-items-center"
          >
            <img
              :src="`${publicPath}/img/rcp/icon/file-download.png`"
              width="35px"
            />
            <div class="d-flex flex-column w-100 px-2">
              <span class="fz-12 fw-500 text-grey3">{{
                getFileName(item.info.fileURL)
              }}</span>
              <span class="fz-10 fw-500 text-grey4">{{
                formatBytes(item.info.fileSize)
              }}</span>
            </div>
            <img
              :src="`${publicPath}/img/rcp/icon/download2.png`"
              width="34px"
              class="ml-auto"
            />
          </a>
          <div>{{ getMessage(item.message, item.info.encodeURL) }}</div>
        </div>

        <!-- Handle Pure URL Messages (entire message is just a URL) -->
        <div v-else-if="isURL(item.message) && !hasTextWithURL(item.message)">
          <!-- Internal Link - Use router-link -->
          <router-link
            v-if="isInternalLink(item.message)"
            :to="getInternalPath(item.message)"
            class="chat-link internal-link"
          >
            <div class="link-preview">
              <div class="link-icon">
                <i
                  class="fas fa-link"
                  style="font-size: 20px; color: #3b82f6"
                ></i>
              </div>
              <div class="link-content">
                <div class="link-title">{{ getLinkTitle(item.message) }}</div>
                <div class="link-url">{{ item.message }}</div>
              </div>
              <div class="link-arrow">
                <img
                  :src="`${publicPath}/img/rcp/icon/arrow-right.png`"
                  width="16px"
                  alt="arrow"
                />
              </div>
            </div>
          </router-link>

          <!-- External Link - Use regular anchor tag -->
          <a
            v-else
            :href="item.message"
            target="_blank"
            rel="noopener noreferrer"
            class="chat-link external-link"
          >
            <div class="link-preview">
              <div class="link-icon">
                <img
                  :src="`${publicPath}/img/rcp/icon/external-link.png`"
                  width="20px"
                  alt="external link"
                />
              </div>
              <div class="link-content">
                <div class="link-title">{{ getLinkTitle(item.message) }}</div>
                <div class="link-url">{{ item.message }}</div>
              </div>
              <div class="link-arrow">
                <img
                  :src="`${publicPath}/img/rcp/icon/external-arrow.png`"
                  width="16px"
                  alt="external arrow"
                />
              </div>
            </div>
          </a>
        </div>

        <!-- Handle Mixed Text + URL Messages -->
        <div v-else-if="hasTextWithURL(item.message)">
          <span
            v-for="(part, partIndex) in parseMessageWithLinks(item.message)"
            :key="`${index}-${partIndex}`"
          >
            <!-- Text part -->
            <span v-if="part.type === 'text'">{{ part.content }}</span>

            <!-- Internal Link part -->
            <router-link
              v-else-if="part.type === 'internal-link'"
              :to="getInternalPath(part.content)"
              class="inline-internal-link"
            >
              {{ part.content }}
            </router-link>

            <!-- External Link part -->
            <a
              v-else-if="part.type === 'external-link'"
              :href="part.content"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-external-link"
            >
              {{ part.content }}
            </a>
          </span>
        </div>

        <!-- Regular Text Message (no URLs) -->
        <div v-else>
          <!-- Check if message contains HTML tags -->
          <div
            v-if="containsHTMLTags(item.message)"
            v-html="sanitizeHTML(item.message)"
            class="html-message"
          ></div>
          <span v-else>{{ item.message }}</span>
        </div>
      </div>

      <img
        v-if="item.user_type == 'member'"
        src="http://ssl.gstatic.com/accounts/ui/avatar_2x.png"
        class="img-circle img-thumbnail p-0 avatar ml-2"
        alt="avatar"
      />
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      publicPath: process.env.BASE_URL != "/" ? process.env.BASE_URL : "",
      messages: [],
      // Define internal domains that should use router-link
      internalDomains: [
        "rcpaylink.com",
        "d-card.rcpaylink.com",
        "elite.rcpaylink.com",
        "epocard.rcpaylink.com",
        "vp.rcpaylink.com",
        "mkcard.rcpaylink.com",
        "junca.rcpaylink.com",
        "irx.rcpaylink.com",
        "rc365.rcpaylink.com",
        "gvv.rcpaylink.com",
        "pinnacle.rcpaylink.com",
        "jp.rcpaylink.com",
      ],
      // Route mappings for internal paths
      routeMappings: {
        "card-replacement": "card-replacement",
        "card-management": "card-management",
        dashboard: "dashboard",
        profile: "profile",
        wallet: "wallet",
        transaction: "transaction",
      },
      // Check if partner theme is active
      isPartnerTheme: false,
      // ✅ ADD: Role tracking
      role: null,
    };
  },
  props: ["list"],

  // ✅ ADD: Computed property for role check (similar to PartnerProfile.vue)
  computed: {
    isPartner() {
      return this.role === "partner";
    },
    // Dynamic classes based on theme
    adminMessageClass() {
      return this.isPartner ? "admin-partner-theme" : "";
    },
    memberMessageClass() {
      return this.isPartner ? "member-partner-theme" : "";
    },
  },

  methods: {
    getFileName(url) {
      let arr = url.split("/");
      return arr[arr.length - 1];
    },

    getFileType(url) {
      let indexImage = this.indexOfImage(url);
      let indexPDF = url.indexOf(".pdf");
      let type = "";

      if (indexImage != -1) {
        type = "image";
      } else if (indexPDF != -1) {
        type = "pdf";
      }

      return type;
    },

    indexOfImage(url) {
      let index = url.indexOf(".png");
      index = index == -1 ? url.indexOf(".jpg") : index + 4;
      index = index == -1 ? url.indexOf(".jpeg") : index + 4;
      index = index == -1 ? url.indexOf(".gif") : index + 5;

      return index != -1 ? index + 4 : index;
    },

    getMessage(str_message, encodeURL) {
      let ret = str_message.replace(encodeURL, "");
      ret = ret.split(/(\\|\/)/g).pop();
      return ret;
    },

    formatBytes(bytes, decimals = 2) {
      if (!+bytes) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    },

    // Check if message is a URL (entire message is just a URL)
    isURL(message) {
      const urlRegex =
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/i;
      const simpleUrlRegex = /^https?:\/\/.+$/i;
      return (
        simpleUrlRegex.test(message.trim()) || urlRegex.test(message.trim())
      );
    },

    // Check if message contains both text and URLs
    hasTextWithURL(message) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const urls = message.match(urlRegex);

      if (!urls) return false;

      // If the entire message is just URLs (separated by spaces), return false
      const textWithoutUrls = message.replace(urlRegex, "").trim();
      return textWithoutUrls.length > 0;
    },

    // Parse message with mixed text and links
    parseMessageWithLinks(message) {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = urlRegex.exec(message)) !== null) {
        // Add text before URL
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            content: message.substring(lastIndex, match.index),
          });
        }

        // Add URL
        const url = match[0];
        parts.push({
          type: this.isInternalLink(url) ? "internal-link" : "external-link",
          content: url,
        });

        lastIndex = urlRegex.lastIndex;
      }

      // Add remaining text after last URL
      if (lastIndex < message.length) {
        parts.push({
          type: "text",
          content: message.substring(lastIndex),
        });
      }

      return parts;
    },

    // Check if URL is internal (should use router-link)
    isInternalLink(url) {
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;

        // Check if hostname matches any internal domains
        return this.internalDomains.some(
          (domain) => hostname === domain || hostname.endsWith("." + domain)
        );
      } catch (e) {
        // If URL parsing fails, treat as external
        return false;
      }
    },

    // Extract internal path for router-link
    getInternalPath(url) {
      try {
        const urlObj = new URL(url);
        let pathname = urlObj.pathname;

        // Remove leading slash if present
        if (pathname.startsWith("/")) {
          pathname = pathname.substring(1);
        }

        // Handle specific route mappings
        const pathParts = pathname.split("/");
        const mainPath = pathParts[0];

        if (this.routeMappings[mainPath]) {
          // If we have parameters (like ODYx in card-replacement/ODYx)
          if (pathParts.length > 1) {
            return {
              name: this.routeMappings[mainPath],
              params: { id: pathParts[1] },
            };
          } else {
            return { name: this.routeMappings[mainPath] };
          }
        }

        // Default: use the pathname as is
        return pathname || "/";
      } catch (e) {
        console.error("Error parsing internal URL:", e);
        return "/";
      }
    },

    // Get link title for display
    getLinkTitle(url) {
      try {
        const urlObj = new URL(url);
        let pathname = urlObj.pathname;

        // Remove leading slash
        if (pathname.startsWith("/")) {
          pathname = pathname.substring(1);
        }

        const pathParts = pathname.split("/");
        const mainPath = pathParts[0];

        // Generate readable titles
        const titles = {
          "card-replacement": "Card Replacement",
          "card-management": "Card Management",
          dashboard: "Dashboard",
          profile: "Profile",
          wallet: "Wallet",
          transaction: "Transaction History",
        };

        if (titles[mainPath]) {
          return pathParts.length > 1
            ? `${titles[mainPath]} - ${pathParts[1]}`
            : titles[mainPath];
        }

        // Default: capitalize and format path
        return (
          mainPath
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ") || "Link"
        );
      } catch (e) {
        return "Link";
      }
    },

    // Check if message contains HTML tags
    containsHTMLTags(message) {
      const htmlTagRegex = /<[^>]+>/;
      return htmlTagRegex.test(message);
    },

    // Sanitize HTML for safe rendering
    sanitizeHTML(html) {
      // Allow only safe HTML tags like <br>, <strong>, <em>, <p>, <div>
      const allowedTags = ["br", "strong", "b", "em", "i", "p", "div", "span"];

      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Remove all script tags and their content
      const scripts = tempDiv.getElementsByTagName("script");
      for (let i = scripts.length - 1; i >= 0; i--) {
        scripts[i].remove();
      }

      // Remove all elements that are not in allowed tags
      const allElements = tempDiv.getElementsByTagName("*");
      for (let i = allElements.length - 1; i >= 0; i--) {
        const tagName = allElements[i].tagName.toLowerCase();
        if (!allowedTags.includes(tagName)) {
          // Replace with text content instead of removing
          const textNode = document.createTextNode(allElements[i].textContent);
          allElements[i].parentNode.replaceChild(textNode, allElements[i]);
        } else {
          // Remove all attributes from allowed tags for security
          const element = allElements[i];
          while (element.attributes.length > 0) {
            element.removeAttribute(element.attributes[0].name);
          }
        }
      }

      return tempDiv.innerHTML;
    },

    // ✅ ADD: Load role from localStorage (similar to PartnerProfile.vue)
    loadRole() {
      try {
        this.role = localStorage.getItem("role");
        this.isPartnerTheme = this.role === "partner";
        console.log("🎨 ChatList: Loaded role from localStorage:", this.role);
      } catch (error) {
        console.error(
          "❌ ChatList: Error loading role from localStorage:",
          error
        );
        this.role = "member";
        this.isPartnerTheme = false;
      }
    },

    // ✅ ADD: Handle role changes (similar to PartnerProfile.vue)
    handleRoleChange(event) {
      console.log("🎭 ChatList: Role change detected", event.detail);

      // Update role
      this.loadRole();

      // Force reactivity update
      this.$forceUpdate();
    },

    // ✅ ADD: Handle CSS loaded events (similar to PartnerProfile.vue)
    handleCssLoaded(event) {
      console.log("🎨 ChatList: CSS loaded", event.detail);

      // Force re-render after CSS is loaded
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },

    // Check if partner theme is active (keep for backward compatibility)
    checkPartnerTheme() {
      this.isPartnerTheme = localStorage.getItem("role") === "partner";
    },
  },

  mounted: function () {
    this.messages = this.list;

    // ✅ ADD: Load role on mount (similar to PartnerProfile.vue)
    this.loadRole();
    console.log("🚀 ChatList: Component mounted with role:", this.role);

    // ✅ ADD: Listen for role changes
    window.addEventListener("roleChanged", this.handleRoleChange);

    // ✅ ADD: Listen for CSS loaded events
    window.addEventListener("cssLoaded", this.handleCssLoaded);

    // Keep for backward compatibility
    this.checkPartnerTheme();
  },

  // ✅ ADD: Initialize role on created hook (similar to PartnerProfile.vue)
  created() {
    this.loadRole();
  },

  // ✅ ADD: Cleanup event listeners (similar to PartnerProfile.vue)
  beforeDestroy() {
    window.removeEventListener("roleChanged", this.handleRoleChange);
    window.removeEventListener("cssLoaded", this.handleCssLoaded);
  },
};
</script>

<style scoped src="@/assets/css/chat.css"></style>

<style scoped>
/* Enhanced Admin Message Styling - Only for Partner Theme */
.admin-partner-theme .message {
  background: linear-gradient(135deg, #192534 0%, #354150 100%) !important;
  color: #ffffff !important;
  border: 1px solid rgba(248, 198, 23, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1) !important;
  position: relative;
  overflow: hidden;
}

/* Add subtle gold accent border on admin messages - Only for Partner Theme */
.admin-partner-theme .message::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(to bottom, #f8c617, #e6b315);
  border-radius: 2px 0 0 2px;
}

/* Enhanced hover effect for admin messages - Only for Partner Theme */
.admin-partner-theme .message:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5), 0 4px 8px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15), 0 0 0 1px rgba(248, 198, 23, 0.4) !important;
  transition: all 0.3s ease;
}

/* Admin avatar enhancement - Only for Partner Theme */
.admin-partner-theme .avatar {
  box-shadow: 0 0 0 3px rgba(248, 198, 23, 0.3), 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 2px solid #f8c617;
  transition: all 0.3s ease;
}

.admin-partner-theme .avatar:hover {
  box-shadow: 0 0 0 3px rgba(248, 198, 23, 0.5),
    0 6px 20px rgba(248, 198, 23, 0.2);
  transform: scale(1.05);
}

/* Link Preview Styles - Updated for dark theme */
.chat-link {
  display: block;
  text-decoration: none;
  color: inherit;
  margin: 8px 0;
}

.link-preview {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid #354150;
  border-radius: 8px;
  background: rgba(25, 37, 52, 0.8);
  transition: all 0.2s ease;
}

.chat-link:hover .link-preview {
  background: rgba(53, 65, 80, 0.9);
  border-color: #f8c617;
  text-decoration: none;
}

.internal-link .link-preview {
  border-left: 4px solid #f8c617;
}

.external-link .link-preview {
  border-left: 4px solid #10b981;
}

.link-icon {
  flex-shrink: 0;
  margin-right: 12px;
}

.link-content {
  flex: 1;
  min-width: 0;
}

.link-title {
  font-weight: 600;
  font-size: 14px;
  color: #ffffff;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.link-url {
  font-size: 12px;
  color: #8a92a6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.link-arrow {
  flex-shrink: 0;
  margin-left: 8px;
}

/* Inline Link Styles - Updated for dark theme */
.inline-internal-link {
  color: #f8c617;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px !important;
}

.inline-internal-link:hover {
  color: #e6b315;
  text-decoration: none;
}

.inline-external-link {
  color: #10b981;
  text-decoration: underline;
  cursor: pointer;
}

.inline-external-link:hover {
  color: #047857;
  text-decoration: none;
}

/* Member message styling to contrast with admin */
.member .message {
  background: rgba(238, 238, 238, 0.95) !important;
  color: #212121 !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
}

.member .avatar {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3), 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

/* Animation for admin messages - Only for Partner Theme */
@keyframes adminMessageGlow {
  0% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 0 0 1px rgba(248, 198, 23, 0.2);
  }
  100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
}

.admin-partner-theme .message {
  animation: adminMessageGlow 3s ease-in-out infinite;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .link-preview {
    padding: 10px;
  }

  .link-title {
    font-size: 13px;
  }

  .link-url {
    font-size: 11px;
  }

  .admin-partner-theme .avatar {
    border-width: 1px;
    box-shadow: 0 0 0 2px rgba(248, 198, 23, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2);
  }
}

/* HTML Message Styling */
.html-message {
  line-height: 1.5;
  word-wrap: break-word;
}

.html-message br {
  margin-bottom: 4px;
}

.html-message strong,
.html-message b {
  color: #f8c617;
  font-weight: 700;
}

.html-message em,
.html-message i {
  font-style: italic;
  color: #ffffff;
}

.html-message p {
  margin-bottom: 8px;
}

.html-message div {
  margin-bottom: 4px;
}

/* Admin HTML message specific styling - Only for Partner Theme */
.admin-partner-theme .html-message strong,
.admin-partner-theme .html-message b {
  color: #f8c617;
}

.admin-partner-theme .html-message {
  color: #ffffff;
}

/* Member HTML message specific styling */
.member .html-message strong,
.member .html-message b {
  color: #2152ff;
}

.member .html-message {
  color: #212121;
}
</style>
